import { getAllAttributeIds, getFirstSelectableAttributeId } from '../../../Utility/TreeUtility'
import {
  findAllDefaultAssetClassAttributeIds,
  isAssetClassDimension,
} from './liveDashboardAssetClass'
import {
  findAllDefaultGeographyAttributeIds,
  isGeographyDimension,
} from './liveDashboardGeography'
import {
  findDefaultCurrencyAttributeId,
  isCurrencyDimension,
} from './liveDashboardCurrency'

export const LIVE_DASHBOARD_DEFAULT_ATTRIBUTES_BY_DIMENSION_ID = {
  4: [130, 210, 160, 220, 200, 170],
  6: [110],
  7: [5],
}

const collectLeafAttributes = (attributes) => {
  const leaves = []
  const walk = (nodes) => {
    for (const node of nodes || []) {
      if (node.Children?.length) {
        walk(node.Children)
      } else {
        leaves.push(node)
      }
    }
  }
  walk(attributes)
  return leaves
}

export const isClientSegmentDimension = (dimension) => {
  const name = String(dimension?.Name ?? '').toLowerCase()
  const display = String(dimension?.DisplayName ?? '').toLowerCase()
  return name === 'segmentid' || display.includes('client segment')
}

const findAllClientSegmentAttributeIds = (dimension) => {
  const leaves = collectLeafAttributes(dimension?.Attributes)
  const segmentIds = leaves.map((attribute) => attribute.AttributeID)

  if (segmentIds.length > 0) {
    return segmentIds
  }

  return getAllAttributeIds(dimension)
}

const parseCalendarYearFromAttribute = (attribute) => {
  const displayName = String(attribute?.DisplayName ?? '')
  const displayMatch = displayName.match(/\d{4}/)
  if (displayMatch) {
    return parseInt(displayMatch[0], 10)
  }

  const idText = String(attribute?.AttributeID ?? '')
  if (idText.length > 4) {
    return parseInt(idText.slice(-4), 10)
  }

  return NaN
}

/** Most recent YearID leaf whose attribute id starts with "10" (e.g. 102024, 102025). */
export function findMostRecentYearAttributeIdStartingWith10(dimension) {
  const leaves = collectLeafAttributes(dimension?.Attributes)
  const candidates = leaves.filter((attribute) => String(attribute.AttributeID).startsWith('10'))
  if (!candidates.length) {
    return null
  }

  return candidates.reduce((best, candidate) => {
    const bestYear = parseCalendarYearFromAttribute(best)
    const candidateYear = parseCalendarYearFromAttribute(candidate)
    if (Number.isNaN(bestYear) || (!Number.isNaN(candidateYear) && candidateYear > bestYear)) {
      return candidate
    }
    return best
  }, candidates[0]).AttributeID
}

const filterToAvailableAttributeIds = (dimension, attributeIds) => {
  if (!Array.isArray(attributeIds) || attributeIds.length === 0) {
    return attributeIds
  }

  const availableIds = new Set(getAllAttributeIds(dimension))
  const filtered = attributeIds.filter((attributeId) => availableIds.has(attributeId))
  return filtered.length > 0 ? filtered : attributeIds
}

const buildFallbackAttributes = (dimension, getDimensionConfiguration, selectAllHideKey, useWhitelistForSingle) => {
  const dimensionConfiguration = getDimensionConfiguration(dimension.Name)
  const singleSelect = JSON.parse(dimension.Details).singleSelect || dimensionConfiguration?.SingleSelect
  const selectAllAndHide = dimensionConfiguration?.[selectAllHideKey]
  const whitelistedIds = dimensionConfiguration?.WhitelistedAttributeIds

  if (singleSelect) {
    if (useWhitelistForSingle && whitelistedIds?.length > 0) {
      return [whitelistedIds[0]]
    }
    return [getFirstSelectableAttributeId(dimension) ?? dimension.Attributes?.[0]?.AttributeID].filter(Boolean)
  }

  if (selectAllAndHide) {
    return getAllAttributeIds(dimension)
  }

  return []
}

/**
 * Default filter selections for the market snapshot chart.
 * Geography, asset class, and client segment default to all dashboard options selected.
 * Year (dimension 3) resolves to the latest calendar year whose YearID starts with "10".
 */
export function buildLiveDashboardDefaultSelections(
  dimensions,
  getDimensionConfiguration,
  selectAllHideKey,
  options = {},
) {
  const { includeAvoidOverlap = false, useWhitelistForSingle = false } = options

  return dimensions.map((dimension) => {
    const dimensionConfiguration = getDimensionConfiguration(dimension.Name)
    const resolvedDimensionId = dimension.OriginalDimensionID ?? dimension.DimensionID
    let attributes = LIVE_DASHBOARD_DEFAULT_ATTRIBUTES_BY_DIMENSION_ID[resolvedDimensionId]

    if (isGeographyDimension(dimension)) {
      attributes = findAllDefaultGeographyAttributeIds(dimension)
      attributes = filterToAvailableAttributeIds(dimension, attributes)
    } else if (isAssetClassDimension(dimension)) {
      attributes = findAllDefaultAssetClassAttributeIds(dimension)
      attributes = filterToAvailableAttributeIds(dimension, attributes)
    } else if (isClientSegmentDimension(dimension)) {
      attributes = findAllClientSegmentAttributeIds(dimension)
      attributes = filterToAvailableAttributeIds(dimension, attributes)
    } else if (isCurrencyDimension(dimension)) {
      const defaultCurrencyId = findDefaultCurrencyAttributeId(dimension)
      attributes = defaultCurrencyId != null ? [defaultCurrencyId] : buildFallbackAttributes(
        dimension,
        getDimensionConfiguration,
        selectAllHideKey,
        useWhitelistForSingle,
      )
    } else if (resolvedDimensionId === 3) {
      const latestYearId = findMostRecentYearAttributeIdStartingWith10(dimension)
      attributes = latestYearId != null ? [latestYearId] : [102024]
    } else if (attributes == null) {
      attributes = buildFallbackAttributes(
        dimension,
        getDimensionConfiguration,
        selectAllHideKey,
        useWhitelistForSingle,
      )
    } else {
      attributes = filterToAvailableAttributeIds(dimension, attributes)
    }

    const minSelections = Number.isFinite(dimensionConfiguration.MinSelections) ? dimensionConfiguration.MinSelections : 0
    const maxSelections = Number.isFinite(dimensionConfiguration.MaxSelections)
      ? dimensionConfiguration.MaxSelections
      : Number.MAX_SAFE_INTEGER
    const isValid = attributes.length >= minSelections && attributes.length <= maxSelections

    const row = {
      DimensionID: dimension.DimensionID,
      IsValid: resolvedDimensionId === 3 ? attributes.length > 0 : isValid,
      Attributes: attributes,
    }

    if (includeAvoidOverlap) {
      row.IsAvoidParentOverlap = !dimensionConfiguration?.SingleSelect && dimensionConfiguration?.AvoidParentOverlap
    }

    return row
  })
}

export function isLiveDashboardChart(chart) {
  return (chart?.title ?? chart?.name ?? '').trim().toLowerCase() === 'market snapshot'
}
